import {AnimatePresence, motion} from 'framer-motion'
import {useCallback, useEffect, useRef} from 'react'

import {useEscapeKey} from '../../../hooks'
import {FadeInOutAnimation} from '../../../animations'
import {useCloseDialogOnBrowserBack} from '../../../hooks/useCloseDialogOnBrowserBack'
import {TocHeader} from './parts/TocHeader'
import {TocPanel} from './parts/TocPanel'

import type {SidePanelRootProps} from '../SidePanelRoot'

export const Toc = (props: SidePanelRootProps) => {
  const {open, onClose, closeOnBrowserBack, heading, children} = props
  const focusGuardRef = useRef<HTMLDivElement | null>(null)

  const handleClose = useCallback((): void => {
    onClose(false)
  }, [onClose])

  useEscapeKey(handleClose)
  useCloseDialogOnBrowserBack({onClose, closeOnBrowserBack, open})
  useEffect(() => {
    if (open && focusGuardRef.current) {
      focusGuardRef.current.focus()
    }
  }, [open])

  return (
    <AnimatePresence>
      {/*
        We don't use the headlessui Dialog component here because on safari/iOS the scroll position
        is restored after closing the dialog
        https://github.com/tailwindlabs/headlessui/issues/3484
        https://github.com/tailwindlabs/headlessui/issues/2801
       */}
      {open && (
        <div
          className={'fixed inset-0 z-[100] overflow-hidden'}
          role={'dialog'}
          aria-labelledby={'toc-title'}
        >
          {/* focus guard */}
          <div
            ref={focusGuardRef}
            tabIndex={0}
            className={'fixed size-0'}
          >
          </div>
          {/* Backdrop */}
          <motion.div
            onClick={handleClose}
            className={'absolute inset-0 z-10 bg-scrim-primary/45'}
            {...FadeInOutAnimation}
          />
          <TocPanel>
            <TocHeader
              heading={heading}
              onClose={handleClose}
            />
            <div className={'sdox-scrollbar min-h-0 overflow-y-auto'}>
              <div className={'p-spacing-36 pb-spacing-64 md:p-spacing-48'}>
                {children}
              </div>
            </div>
          </TocPanel>
        </div>
      )}
    </AnimatePresence>
  )
}
